import type { ApiErrorDetail } from './types'

/**
 * Chyba z API.
 *
 * `messageSk` je text, ktorý sa smie ukázať používateľovi — posiela ho server,
 * frontend si vlastné hlášky nevymýšľa (jediná výnimka je výpadok siete nižšie,
 * kde odpoveď neexistuje). `code` je strojový a používa sa na vetvenie.
 */
export class ApiError extends Error {
	constructor(
		public readonly status: number,
		public readonly code: string,
		public readonly messageSk: string,
		public readonly details: readonly ApiErrorDetail[] = [],
	) {
		super(`${code} (HTTP ${status}): ${messageSk}`)
		this.name = 'ApiError'
	}

	/** Chyby validácie sa vypisujú k poliam, nie do toastu. */
	isValidation(): boolean {
		return this.status === 422
	}

	/** Chyby, ktoré nemá zmysel opakovať — opakovanie skončí rovnako. */
	isPermanent(): boolean {
		return this.status >= 400 && this.status < 500 && this.status !== 429
	}
}

/** Relácia vypršala a obnovenie tokenu neprešlo — používateľ musí znova prihlásiť. */
export class UnauthorizedError extends ApiError {
	constructor(messageSk = 'Vaše prihlásenie vypršalo. Prihláste sa znova.') {
		super(401, 'UNAUTHORIZED', messageSk)
		this.name = 'UnauthorizedError'
	}
}

/** Server neodpovedal — sieť, DNS, spadnutý kontajner. Odpoveď neexistuje. */
export class NetworkError extends Error {
	readonly messageSk = 'Server neodpovedá. Skontrolujte pripojenie a skúste to znova.'

	constructor(public override readonly cause: unknown) {
		super('Sieťová chyba pri volaní API')
		this.name = 'NetworkError'
	}
}

/**
 * Text pre používateľa z ľubovoľnej chyby.
 *
 * Používa ho toast aj chybový stav stránky, aby sa na dvoch miestach
 * nerozhodovalo, čo je „bezpečné“ ukázať.
 */
export function messageSkFrom(error: unknown): string {
	if (error instanceof ApiError) {
		return error.messageSk
	}

	if (error instanceof NetworkError) {
		return error.messageSk
	}

	return 'Nastala neočakávaná chyba. Skúste to prosím znova.'
}
